import React, { useState, useEffect, useRef } from 'react';
import { dashboardAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Star, CheckCircle2, Award, UsersRound, ShoppingBag, Target, Activity } from 'lucide-react';

const EVENT_CONFIG = {
  level_up: { icon: Star, color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  activity_completed: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50', border: 'border-green-200' },
  achievement: { icon: Award, color: 'text-yellow-500', bg: 'bg-yellow-50', border: 'border-yellow-200' },
  group_award: { icon: UsersRound, color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' },
  purchase: { icon: ShoppingBag, color: 'text-purple-500', bg: 'bg-purple-50', border: 'border-purple-200' },
  subscription: { icon: Target, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200' },
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'ahora';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `hace ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'ayer';
  return `hace ${days}d`;
}

export default function ActivityFeed({ classId, user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);

  useEffect(() => {
    const loadFeed = async () => {
      try {
        const res = await dashboardAPI.getActivityFeed(classId, 25);
        setEvents(res.data || []);
      } catch {} finally { setLoading(false); }
    };
    loadFeed();
  }, [classId]);

  // Listen for real-time feed events via WebSocket
  useEffect(() => {
    if (!user?.token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/notifications?token=${user.token}`);
    
    ws.onopen = () => {
      wsRef.current = ws;
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
      ws._pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'feed_event' && data.event) {
          setEvents(prev => [data.event, ...prev].slice(0, 30));
        }
      } catch {}
    };
    
    ws.onclose = () => {
      if (ws._pingInterval) clearInterval(ws._pingInterval);
      wsRef.current = null;
    };
    ws.onerror = () => ws.close();
    
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [user?.token]);

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="activity-feed">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" />
          Actividad Reciente
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        {events.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Sin actividad reciente</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-slate-200" />
            
            <div className="space-y-1">
              {events.map((event, idx) => {
                const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.subscription;
                const Icon = config.icon;
                const isNew = idx === 0 && (Date.now() - new Date(event.created_at).getTime()) < 60000;
                
                return (
                  <div 
                    key={event.id} 
                    className={`flex items-start gap-3 py-1.5 pl-0 pr-1 relative transition-all ${isNew ? 'animate-pulse' : ''}`}
                    data-testid={`feed-event-${event.id}`}
                  >
                    <div className={`w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 z-10 border ${config.bg} ${config.border}`}>
                      <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm text-slate-700 leading-snug">{event.message}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(event.created_at)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
